/**
 * 登录 / 注册页外框:
 *   桌面 = 左半屏绿色品牌区 (Logo · 产品名 · 3 个卖点 · 细点纹理) + 右半屏白底表单
 *   手机 = 品牌区收成顶部绿色横幅, 表单卡片上移压住横幅底边
 */
import type { ReactNode } from "react";
import { Calculator, ImageIcon, LayoutTemplate, Zap, type LucideIcon } from "lucide-react";
import { PRODUCT_NAME } from "@/components/layout/BrandMark";
import { cn } from "@/lib/utils";

const POINTS: Array<{ icon: LucideIcon; title: string; desc: string }> = [
  { icon: Zap, title: "秒级报价", desc: "一句话描述材质、尺寸和数量，自动识别参数并算出价格" },
  { icon: ImageIcon, title: "AI 画图", desc: "按尺寸生成海报、门头、菜单图，一键下载" },
  { icon: LayoutTemplate, title: "模板排版", desc: "AI 生成网页模板，拖选局部修改，渲染为高清图片" },
];

/** 深一档的绿 #2E7D32 → #388E3C + 细点纹理 (纯 CSS, 固定色值不随主题变) */
const BRAND_BG =
  "bg-[radial-gradient(rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(135deg,#2e7d32_0%,#388e3c_100%)] bg-[length:22px_22px,100%_100%]";

function Logo({ size = "md" }: { size?: "sm" | "md" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-xl bg-white/20 text-white ring-1 ring-white/30",
        size === "md" ? "h-11 w-11" : "h-9 w-9",
      )}
    >
      <Calculator className={size === "md" ? "h-6 w-6" : "h-5 w-5"} strokeWidth={2.25} />
    </span>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-background lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* 桌面品牌区 (左半屏) */}
      <aside className={cn("relative hidden overflow-hidden text-white lg:flex lg:flex-col", BRAND_BG)}>
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/[0.07]" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-white/[0.05]" />
        <div className="relative flex flex-1 flex-col justify-between px-12 py-10 xl:px-16">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-lg font-semibold tracking-tight [text-shadow:0_1px_2px_rgba(0,0,0,0.12)]">{PRODUCT_NAME}</span>
          </div>

          <div className="max-w-md [text-shadow:0_1px_2px_rgba(0,0,0,0.12)]">
            <h2 className="text-3xl font-semibold leading-tight tracking-tight xl:text-4xl">
              报价、出图、排版
              <br />
              一个助手全搞定
            </h2>
            <p className="mt-3 text-base text-white/95">为广告制作门店打造的 AI 工作台</p>
            <ul className="mt-10 space-y-5">
              {POINTS.map((p) => (
                <li key={p.title} className="flex gap-3.5">
                  <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/25">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-semibold">{p.title}</div>
                    <div className="mt-0.5 text-sm leading-relaxed text-white/95">{p.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-caption text-white/85">© {new Date().getFullYear()} {PRODUCT_NAME}</p>
        </div>
      </aside>

      {/* 表单区 (右半屏) */}
      <main className="flex min-h-[100dvh] flex-col bg-card max-lg:bg-background lg:min-h-0 lg:items-center lg:justify-center lg:px-12 lg:py-10">
        {/* 手机品牌横幅 */}
        <div className={cn("px-5 pb-14 pt-[max(2rem,env(safe-area-inset-top))] text-white lg:hidden", BRAND_BG)}>
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <div className="min-w-0">
              <div className="text-base font-semibold tracking-tight">{PRODUCT_NAME}</div>
              <div className="text-caption text-white/90">秒级报价 · AI 画图 · 模板排版</div>
            </div>
          </div>
        </div>
        <div className="-mt-8 flex w-full flex-1 items-start justify-center px-4 pb-8 lg:mt-0 lg:flex-none lg:px-0 lg:pb-0">
          {/* 手机: 白色卡片压住横幅; 桌面: 右半屏本身是白底, 不再套卡片 */}
          <div className="w-full max-w-[400px] max-lg:rounded-2xl max-lg:border max-lg:bg-card max-lg:p-6 max-lg:shadow-lg max-lg:shadow-black/[0.04]">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

/** 表单内容 (标题 + 副标题 + 内容)。外层已有白底 (桌面右半屏 / 手机卡片), 这里不再套卡片; 输入框/主按钮加高 */
export function AuthCard({ title, subtitle, children }: { title: ReactNode; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="[&_input]:h-11 [&_input]:text-[15px] [&_button[type=submit]]:h-11 [&_button[type=submit]]:text-[15px]">
      <div className="mb-7 space-y-1.5">
        <h1 className="text-[26px] font-bold leading-tight tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
